import type { ReactNode } from "react";

/*
 * Der Breitenwechsel: schmale Darstellung unter dem Umbruch, breite ab ihm.
 *
 * ═══ WARUM DIESE DATEI ÜBERHAUPT EXISTIERT ═══════════════════════════════
 *
 * Weil die Zusage, die daran hängt, sonst in jeder Sicht neu geschrieben
 * werden müsste — und in der zweiten schon fehlte. Der Bestand belegt das:
 * er wechselt an drei verschiedenen Breiten (`/suche`, `/totes-kapital`,
 * `/lieferant` bei `lg`; `/verkaufssuche` und die Detailseite bei `sm`;
 * Heatmap und Navigation bei `md`) und hat für keinen dieser Wechsel eine
 * einzige Zusicherung. Nachgesehen, nicht vermutet.
 *
 * Hier steht der Umbruch an EINER Stelle. Wer ihn verschiebt, verschiebt ihn
 * für alle Sichten, und `Breitenwechsel.test.tsx` hält fest, dass beide
 * Hälften denselben Umbruch benutzen.
 *
 * ═══ Standard und referenzgetreue Ausnahmen ══════════════════════════════
 *
 * `lg` (1024 px) bleibt die Vorgabe für `/suche`. Verkaufssuche und
 * Detailseite wählen ausdrücklich `sm` (640 px), weil ihre Referenz die
 * vollständige Tabelle ab dieser Breite horizontal scrollbar anbietet.
 * Beide Hälften jeder Variante bleiben hier zentral gekoppelt; ein Aufrufer
 * kann also nie nur eine Hälfte auf einen anderen Umbruch setzen.
 *
 * ═══ DIE ZAHLEN, die `lg` begründen ══════════════════════════════════════
 *
 * Waagerechter Überlauf des Tabellen-Containers (`scrollWidth − clientWidth`
 * am `role="region"`), im Ist-Zustand ohne Karten gemessen:
 *
 *                            Container   Inhalt   Überlauf
 *     /verkaufssuche   768 px    734        924      190
 *     /verkaufssuche  1024 px    990        990        0
 *     /verkaufssuche  1280 px   1246       1246        0
 *
 * Die seit der Angleichung ans Altrepo 17-spaltige `/suche`-Tabelle nutzt
 * feste Prozentbreiten und bleibt wie ihre Referenz auf der Containerbreite;
 * bei 1280 px wurde deshalb 0 px Überlauf gemessen. Ihr Wechsel bei `lg`
 * bildet den mobilen Karten-/breiten Tabellenvertrag des Altrepos ab. Den
 * numerischen Engpass für den gemeinsamen Umbruch bestimmt weiterhin die
 * Verkaufssicht.
 *
 * **Der schlagende Wert ist die 0 bei 1024.** Die Verkaufstabelle passt dort
 * bei exakt 990 px hinein — `lg` ist also nicht irgendein gewählter Umbruch,
 * sondern die Breite, ab der diese Tabelle zu passen BEGINNT. Bei 768 px
 * fehlen 190 px: ein Tablet-Nutzer muss waagerecht scrollen, um die letzte
 * Spalte überhaupt zu finden.
 *
 * Damit ist der naheliegende Einwand widerlegt, und er wurde ausdrücklich
 * erhoben: die 7-spaltige Verkaufstabelle sei auf einem Tablet „gedrängt,
 * aber brauchbar". Sie ist dort nicht vollständig sichtbar. Der Bestand gibt
 * dem Tablet mit seinem `sm` eine Tabelle, deren rechte Spalten man erst
 * suchen muss.
 *
 * ═══ ACHTUNG: DER ERSTE GRUND TRÄGT NICHT FÜR JEDE SICHT ═════════════════
 *
 * Die Tabelle oben begründet `lg` damit, dass die Verkaufstabelle dort zu
 * passen BEGINNT — die 0 bei 1024. **Für die Sichten, die seither
 * dazugekommen sind, gilt dieser Satz NICHT**, und wer ihn liest und seine
 * Sicht stillschweigend dazurechnet, hält ihn für belegt, obwohl er es nicht
 * ist. Gemessen am ausgelieferten Artefakt:
 *
 *                             Container   Inhalt   Überlauf
 *     /artikel/$id     320 px      286       1334     1048
 *     /artikel/$id   1.280 px    1246       1334       88
 *
 * `/artikel/$id` passt also auf KEINER geprüften Breite. Der Wechsel bei
 * `sm` behauptet nicht, dass sie dort ohne Scrollen passt; er bildet den
 * Referenzvertrag ab und macht die vollständige Tabelle auf Tablets früher
 * erreichbar. Auf Telefonen bleibt die Kartenansicht.
 *
 * **Eine datierte Begründung, die um eine Sicht erweitert wird, ohne dass
 * jemand prüft, ob sie dort noch gilt, ist der Mechanismus, der in diesem
 * Repo schon dreimal etwas gekostet hat.** Wer eine dritte Sicht anschliesst,
 * misst nach und schreibt das Ergebnis hierher — auch dann, wenn es „passt
 * ebenfalls nicht" lautet.
 *
 * ═══ Und der Grund, der auch ohne die Zahlen gälte ═══════════════════════
 *
 * Ein geteilter Umschalter muss beide Hälften am selben Umbruch schalten.
 * Darum stehen die erlaubten Varianten und ihre Klassen gemeinsam in dieser
 * Datei. Jede Sicht wählt genau eine Variante; deren Messpunkt wird im
 * Browser-Test aus der exportierten Zahl gelesen statt dort dupliziert.
 *
 * Was diese Entscheidung NICHT ist: eine Aussage über die anderen Sichten.
 * Ob eine Sicht einen Wechsel bekommt, entscheidet die Sicht, die ihn baut —
 * sie erbt dann einen zentral definierten Umbruch, und sie schuldet die Messung dafür (der
 * Absatz „DER ERSTE GRUND TRÄGT NICHT FÜR JEDE SICHT" oben sagt, warum).
 *
 * **Hier stand bis Posten 75(b) eine Liste der Sichten ohne Wechsel, und
 * `/artikel` stand darauf.** Sie ist mit dem ersten Nachzügler falsch
 * geworden und war es zwei Absätze lang, während direkt darüber die Messung
 * genau dieser Sicht stand — die Datei hätte sich selbst widersprochen.
 * Solche Aufzählungen sind datierte Aussagen: sie altern still, weil der,
 * der die neue Sicht baut, den Absatz über die ALTEN nicht liest. Deshalb
 * steht hier jetzt die Regel und keine Liste.
 *
 * Wer wissen will, welche Sichten heute wechseln, fragt den Riegel in
 * `Breitenwechsel.test.tsx`: er scannt die Quellen und kann nicht veralten.
 *
 * ═══ BEIDE HÄLFTEN STEHEN IMMER IM DOM ═══════════════════════════════════
 *
 * Kein `useState`, kein `matchMedia`, kein Resize-Horcher. Der Umschalter ist
 * reines CSS, und das ist die tragende Eigenschaft:
 *
 *   - Serverseitig gerendert oder in einem Test ohne Layout gibt es keinen
 *     „noch unbekannten" Zustand, in dem eine Hälfte fehlt oder beide
 *     kurz aufblitzen.
 *   - Ein Media-Query-Hook liest die Breite ERST nach dem ersten Anstrich.
 *     Die erste Bildfolge zeigte dann die falsche Hälfte.
 *   - Es gibt keinen Zustand, der mit der tatsächlichen Fensterbreite
 *     auseinanderlaufen kann.
 *
 * Der Preis ist ehrlich zu nennen: **beide Hälften werden gerendert**, also
 * auch die unsichtbare. Bei einer Liste aus 20 Karten und 20 Tabellenzeilen
 * ist das doppelte Arbeit für React. Das ist vertretbar, solange beide
 * Hälften dieselben Daten zeigen — und genau das ist ohnehin Pflicht, siehe
 * unten.
 *
 * ═══ `hidden` IST `display:none` — und darum ist es hier RICHTIG ═════════
 *
 * Was unter `display:none` liegt, ist aus dem Zugänglichkeitsbaum entfernt.
 * Ein Screenreader findet die verborgene Hälfte nicht, kein Fokus landet
 * darin, keine Suche im Browser trifft sie. Das ist bei einem
 * Karten/Tabellen-Wechsel die gewollte Wirkung: sonst läse ein Screenreader
 * JEDEN Datensatz zweimal, einmal als Karte und einmal als Tabellenzeile.
 *
 * Diese Richtigkeit hat aber eine **Bedingung**, und sie ist der Grund, warum
 * hier so viel Text steht:
 *
 *     Beide Hälften müssen DIESELBEN Daten zeigen.
 *
 * Zeigt eine Hälfte ein Feld, das die andere nicht hat, dann ist dieses Feld
 * für die halbe Nutzerschaft unerreichbar — und zwar STILL, denn beide
 * Hälften sehen für sich plausibel aus. Der Bestand verletzt das
 * nachweislich: vier Felder stehen dort nur auf der Karte, fünf nur in der
 * Tabelle, und die Granularität ist verschieden (Karte je Artikel, Tabelle je
 * Charge).
 *
 * Deshalb reicht diese Datei die Bedingung nicht als Bitte an ihre Aufrufer
 * weiter: deren Tests halten jeweils beide Hälften fest. `/suche` folgt dabei
 * bewusst dem Altrepo-Vertrag — kompakte Artikelkarten unter `lg`, die volle
 * Artikel-/Standort-/Chargentabelle darüber. Die Karten tragen weiterhin die
 * serverseitigen Artikelaggregate; Detailwerte bleiben über den Artikellink
 * erreichbar.
 *
 * ═══ DER HALTER DER AUSSAGE „WAS BLENDET NACH BREITE AUS" ════════════════
 *
 * **Diese Datei ist die einzige Stelle in `src/`, die etwas nach Breite
 * ausblendet — und dieser Absatz ist der einzige Ort, an dem das steht.**
 *
 * Bis zu diesem Posten galt die Zusage ohne Ausnahme. Sie stand als PROSA an
 * DREI Orten (`components/Navigation.tsx` zweimal, `routes/__root.tsx`
 * einmal), jeder mit eigener Formulierung und eigenem Gegenbeweis-Grep. Eine
 * Tatsache, die an drei Orten geführt wird, läuft auseinander; dieses Repo
 * hat daran schon zweimal verloren. Die drei Stellen ZEIGEN deshalb jetzt
 * hierher, statt die Aussage zu wiederholen.
 *
 * Der Unterschied zu dem im Kopf abgelehnten Fall ist genau die Bedingung
 * oben: dort wäre der Name weggeblendet worden, OHNE ihn anderswo zu zeigen.
 * Hier steht die andere Hälfte daneben.
 *
 * ═══ Und die Aussage steht nicht mehr nur als Prosa da ═══════════════════
 *
 * Das war der eigentliche Mangel: die Zusage sprach über die GANZE SPA, und
 * gehalten hat sie eine einzige Prüfung über die className EINES Feldes
 * (`Navigation.test.tsx`, das Namensfeld im Kopf). Prosa breiter als ihr
 * Riegel.
 *
 * `Breitenwechsel.test.tsx` scannt jetzt alle Nicht-Test-Quellen unter
 * `src/` nach Breiten-Umschaltern und hält die Fundmenge gegen genau eine
 * begründete Ausnahme: diese Datei. Wer eine zweite einführt, bekommt einen
 * roten Test mit der Aufforderung, sie hier zu begründen — nicht eine
 * Prosa-Zeile, die niemand liest.
 */

/**
 * Die Umbrüche als Zahlen für Messungen.
 *
 * Das ist der Tailwind-Umbruch `lg`. Die Zahl steht hier, damit der e2e-Fall
 * sie nicht abtippen muss: er misst bei `UMBRUCH_PX - 1` und `UMBRUCH_PX`,
 * und beide Werte kommen aus dieser Konstanten. Wird der Umbruch verschoben,
 * wandert der Messpunkt mit — ein Off-by-one bei 1280 px zeigt nichts.
 */
export const UMBRUCH_PX = 1024;

/** `/verkaufssuche` folgt dem `sm`-Umbruch ihres Altrepos. */
export const VERKAUF_UMBRUCH_PX = 640;

/** Die Artikeldetailseite folgt dem `sm`-Umbruch ihrer Referenz. */
export const DETAIL_UMBRUCH_PX = 640;

/** Der Kopf wechselt wie im Altrepo ab `md` zwischen Menüknopf und Navigation. */
export const NAVIGATION_UMBRUCH_PX = 768;

/**
 * Die Klasse der SCHMALEN Hälfte: sichtbar bis `UMBRUCH_PX - 1`.
 *
 * ═══ Warum das ein ausgeschriebenes Literal ist ══════════════════════════
 *
 * **Tailwind v4 erkennt nur Klassennamen, die als Literal im Quelltext
 * stehen.** Kein Template-String, keine Verkettung aus Variablen, kein
 * `` `${bp}:hidden` ``. Wer den Umbruch aus `UMBRUCH_PX` zusammenbaute,
 * bekäme eine Klasse, die im gebauten CSS **nicht vorkommt** — die Hälften
 * stünden dann beide sichtbar untereinander, und **kein Test würde rot**,
 * weil jsdom kein Tailwind lädt und die Klasse am Knoten trotzdem steht.
 *
 * Dieser Fehler ist in diesem Repo schon gemessen worden. Der Riegel dagegen
 * ist zweiteilig: das Literal hier, und ein Fall in
 * `Breitenwechsel.test.tsx`, der die beiden Literale PARST und vergleicht.
 * Geparst wird, was dasteht; zusammengebaut wird nichts.
 */
const SCHMAL_KLASSE = "lg:hidden";

/**
 * Die Klasse der BREITEN Hälfte: sichtbar ab `UMBRUCH_PX`.
 *
 * `hidden lg:block` und nicht `hidden lg:flex` oder `lg:table`: was die
 * breite Hälfte innen für ein Layout braucht, ist ihre Sache und gehört an
 * ihr eigenes Element. Dieser Halter macht nur sichtbar oder unsichtbar.
 */
const BREIT_KLASSE = "hidden lg:block";

/** Altrepo-Vertrag der Verkaufssuche: Karten nur unter `sm`, dann Tabelle. */
const VERKAUF_SCHMAL_KLASSE = "sm:hidden";
const VERKAUF_BREIT_KLASSE = "hidden sm:block";

/** Mobilmenü unter `md`, vollständige Kopfnavigation darüber. */
const NAVIGATION_SCHMAL_KLASSE = "md:hidden";
const NAVIGATION_BREIT_KLASSE = "hidden md:block";

/**
 * Die beiden Klassen, für den Test lesbar gemacht.
 *
 * Der Test braucht die Literale, um zu prüfen, dass beide denselben Umbruch
 * benutzen und dass sie einander wirklich ausschliessen. Er soll sie dafür
 * nicht abtippen — eine abgetippte Erwartung prüft die Kopie, nicht das
 * Original.
 */
export const BREITENWECHSEL_KLASSEN = {
  schmal: SCHMAL_KLASSE,
  breit: BREIT_KLASSE,
} as const;

export const VERKAUF_BREITENWECHSEL_KLASSEN = {
  schmal: VERKAUF_SCHMAL_KLASSE,
  breit: VERKAUF_BREIT_KLASSE,
} as const;

export const NAVIGATION_BREITENWECHSEL_KLASSEN = {
  schmal: NAVIGATION_SCHMAL_KLASSE,
  breit: NAVIGATION_BREIT_KLASSE,
} as const;

/**
 * Zwei Darstellungen derselben Daten, umgeschaltet nach Fensterbreite.
 *
 * Beide Kinder sind PFLICHT und nicht optional. Ein optionales `schmal` wäre
 * die Einladung, es zu vergessen — und dann zeigte die Sicht auf dem Telefon
 * gar nichts, ohne dass irgendetwas rot würde.
 *
 * @param schmal Die Darstellung unter `UMBRUCH_PX` — in der Regel Karten.
 * @param breit  Die Darstellung ab `UMBRUCH_PX` — in der Regel eine Tabelle.
 */
export function Breitenwechsel({
  schmal,
  breit,
  umbruch = "lg",
}: {
  schmal: ReactNode;
  breit: ReactNode;
  /** `sm` gilt der Verkaufssuche, `md` der Kopfnavigation. */
  umbruch?: "sm" | "md" | "lg";
}) {
  const klassen =
    umbruch === "sm"
      ? VERKAUF_BREITENWECHSEL_KLASSEN
      : umbruch === "md"
        ? NAVIGATION_BREITENWECHSEL_KLASSEN
        : BREITENWECHSEL_KLASSEN;
  return (
    <>
      {/*
       * `min-w-0` an beiden Haltern: beide sind in aller Regel Kinder eines
       * `flex flex-col`, und ein Flex-Kind hat als Mindestbreite die
       * Inhaltsbreite. Ohne `min-w-0` drückte eine breite Tabelle den Halter
       * auf, und das Dokument scrollte seitwärts — auch dann, wenn die
       * Tabelle selbst einen Scroll-Container hat.
       */}
      <div className={`min-w-0 ${klassen.schmal}`}>{schmal}</div>
      <div className={`min-w-0 ${klassen.breit}`}>{breit}</div>
    </>
  );
}
